import { Button, Icon, IconType } from '@assembly-js/design-system';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@material-ui/core';
import { Editor } from '@tiptap/core';
import { useMemo, useState } from 'react';

import { ActionHandlers, createActionHandlers } from './utils';

const ACTION_KEYS = ['heading1', 'heading2', 'heading3', 'text'] as const;

type TextMenuActionKey = (typeof ACTION_KEYS)[number];

interface TextMenuAction extends ActionHandlers {
  label: string;
  icon: IconType;
}

export const TextMenu = ({
  editor,
  actions = [...ACTION_KEYS],
}: {
  editor: Editor;
  actions?: TextMenuActionKey[];
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  let active: TextMenuActionKey = 'text';
  if (editor.isActive('heading', { level: 1 })) {
    active = 'heading1';
  } else if (editor.isActive('heading', { level: 2 })) {
    active = 'heading2';
  } else if (editor.isActive('heading', { level: 3 })) {
    active = 'heading3';
  }

  const textMenuActions = useMemo(
    (): Record<TextMenuActionKey, TextMenuAction> => ({
      heading1: {
        label: 'Heading 1',
        icon: 'H1',
        ...createActionHandlers(editor, (chain) =>
          chain.focus().toggleHeading({ level: 1 }).run(),
        ),
      },
      heading2: {
        label: 'Heading 2',
        icon: 'H2',
        ...createActionHandlers(editor, (chain) =>
          chain.focus().toggleHeading({ level: 2 }).run(),
        ),
      },
      heading3: {
        label: 'Heading 3',
        icon: 'H3',
        ...createActionHandlers(editor, (chain) =>
          chain.focus().toggleHeading({ level: 3 }).run(),
        ),
      },
      text: {
        label: 'Text',
        icon: 'Text',
        ...createActionHandlers(editor, (chain) =>
          chain.focus().setParagraph().run(),
        ),
      },
    }),
    [editor],
  );

  return (
    <>
      <Button
        label={textMenuActions[active].label}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        suffixIcon="ChevronDown"
        variant="secondary"
        className="h-10 rounded-none"
      />
      <Menu
        open={anchorEl !== null}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: -3,
          horizontal: 'left',
        }}
        getContentAnchorEl={null}
        onClose={() => setAnchorEl(null)}
        classes={{
          paper:
            'border border-solid border-secondary-hover shadow-dropdown-menu',
        }}
      >
        {actions.map((key) => {
          const { label, icon, action, canRunAction } = textMenuActions[key];
          return (
            <MenuItem
              key={label}
              onClick={() => {
                action();
                setAnchorEl(null);
              }}
              selected={key === active}
              disabled={key !== active && !canRunAction()}
            >
              <ListItemIcon classes={{ root: 'min-w-8' }}>
                <Icon icon={icon} className="w-4 h-4" />
              </ListItemIcon>
              <ListItemText>{label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
